import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatMonto, formatFecha } from '../utils/format';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// Disponibilidad de la empresa (#87): suma en $ de los saldos de todas las
// cuentas activas. Las cuentas en Bs se convierten a la tasa elegida (BCV o
// USD genérica, la última cargada); otras monedas no se suman y se avisan.
// Clic en la tarjeta: despliega el saldo de cada cuenta en una fila a todo
// lo ancho debajo de las tarjetas (order-last dentro de la grilla del Dashboard).
// `enLinea` (pantalla Inicio): el saldo de cada cuenta va siempre a la vista,
// dentro de la misma tarjeta, sin desplegar.
export default function TarjetaDisponibilidad({ cuentas, tasasHoy, enLinea = false }) {
  const [tasa, setTasa] = useState('BCV');
  const [abierto, setAbierto] = useState(false);

  const activas = cuentas.filter((c) => Number(c.activo) === 1);
  const totalBs = activas.filter((c) => c.moneda === 'BS').reduce((s, c) => s + Number(c.saldo_actual || 0), 0);
  const totalUsd = activas.filter((c) => c.moneda === 'USD').reduce((s, c) => s + Number(c.saldo_actual || 0), 0);
  const otras = activas.filter((c) => c.moneda !== 'BS' && c.moneda !== 'USD');

  const valorTasa = Number(tasa === 'BCV' ? tasasHoy?.tasa_bcv : tasasHoy?.tasa_usd_generica) || 0;
  const faltaTasa = totalBs !== 0 && valorTasa <= 0;
  const total = totalUsd + (valorTasa > 0 ? totalBs / valorTasa : 0);

  const enDolares = (c) => {
    if (c.moneda === 'USD') return Number(c.saldo_actual || 0);
    if (c.moneda === 'BS' && valorTasa > 0) return Number(c.saldo_actual || 0) / valorTasa;
    return null;
  };
  const detalle = [...activas].sort((a, b) => (enDolares(b) ?? -Infinity) - (enDolares(a) ?? -Infinity));
  const saldoEnSuMoneda = (c) => `${c.moneda === 'BS' ? 'Bs ' : c.moneda === 'USD' ? '$' : `${c.moneda} `}${formatMonto(c.saldo_actual)}`;

  if (enLinea) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-slate-500">Disponibilidad total</p>
          <select
            value={tasa}
            onChange={(e) => setTasa(e.target.value)}
            className="border rounded-md px-1.5 py-0.5 text-xs text-slate-600"
            title="Tasa para convertir las cuentas en Bs"
          >
            <option value="BCV">BCV</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <p className="text-2xl font-bold text-slate-800 tabular-nums">${formatMonto(total)}</p>
        {faltaTasa && <p className="text-xs text-amber-600 mt-1">Sin tasa {tasa} cargada: los Bs no están sumados.</p>}
        {otras.length > 0 && (
          <p className="text-xs text-amber-600 mt-1">No incluye {otras.length} cuenta{otras.length === 1 ? '' : 's'} en otra moneda.</p>
        )}
        <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
          {detalle.map((c) => {
            const usd = enDolares(c);
            return (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-700">{c.nombre}</span>
                  {c.moneda !== 'USD' && usd !== null && (
                    <span className="block text-xs text-slate-400 tabular-nums">≈ ${formatMonto(usd)} ({tasa})</span>
                  )}
                </span>
                <span className={`whitespace-nowrap font-semibold tabular-nums ${Number(c.saldo_actual) < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                  {saldoEnSuMoneda(c)}
                </span>
              </li>
            );
          })}
          {detalle.length === 0 && <li className="py-2 text-sm text-slate-400">Sin cuentas activas.</li>}
        </ul>
      </div>
    );
  }

  return (
    <>
    <div
      {...filaClickeable(() => setAbierto((v) => !v))}
      role="button"
      className={`bg-white rounded-xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow ${FOCO_FILA}`}
      title={abierto ? 'Ocultar el saldo de cada cuenta' : 'Ver el saldo de cada cuenta'}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500 flex items-center gap-1">
          Disponibilidad total
          <ChevronDown size={14} className={`transition-transform ${abierto ? 'rotate-180' : ''}`} />
        </p>
        <select
          value={tasa}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setTasa(e.target.value)}
          className="border rounded-md px-1.5 py-0.5 text-xs text-slate-600"
          title="Tasa para convertir las cuentas en Bs"
        >
          <option value="BCV">BCV</option>
          <option value="USD">USD</option>
        </select>
      </div>
      <p className="text-2xl font-bold text-slate-800 tabular-nums">${formatMonto(total)}</p>
      <p className="text-xs text-slate-400 mt-1 tabular-nums">
        Bs {formatMonto(totalBs)} · $ {formatMonto(totalUsd)}
      </p>
      <p className="text-xs text-slate-400">
        {activas.length} cuenta{activas.length === 1 ? '' : 's'} activa{activas.length === 1 ? '' : 's'}
        {valorTasa > 0 && totalBs !== 0 && ` · tasa ${formatMonto(valorTasa)}`}
      </p>
      {faltaTasa && (
        <p className="text-xs text-amber-600 mt-1">Sin tasa {tasa} cargada: los Bs no están sumados.</p>
      )}
      {otras.length > 0 && (
        <p className="text-xs text-amber-600 mt-1">
          No incluye {otras.length} cuenta{otras.length === 1 ? '' : 's'} en otra moneda.
        </p>
      )}
    </div>

    {abierto && (
      <div className="bg-white rounded-xl shadow-sm p-4 md:col-span-2 lg:col-span-3 xl:col-span-5 order-last">
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-left text-xs">
            <tr>
              <th className="py-1.5 pr-2">Cuenta</th>
              <th className="py-1.5 px-2 text-right">Saldo</th>
              <th className="py-1.5 px-2 text-right">En $ ({tasa})</th>
              <th className="py-1.5 pl-2 text-right hidden sm:table-cell">Último movimiento</th>
            </tr>
          </thead>
          <tbody>
            {detalle.map((c) => {
              const usd = enDolares(c);
              return (
                <tr key={c.id} className="border-t">
                  <td className="py-1.5 pr-2">{c.nombre}</td>
                  <td className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums">{saldoEnSuMoneda(c)}</td>
                  <td className="py-1.5 px-2 text-right whitespace-nowrap tabular-nums">{usd !== null ? `$${formatMonto(usd)}` : '-'}</td>
                  <td className="py-1.5 pl-2 text-right whitespace-nowrap text-slate-500 hidden sm:table-cell">
                    {c.ultima_fecha ? formatFecha(c.ultima_fecha) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t font-medium">
              <td className="py-1.5 pr-2">Total</td>
              <td />
              <td className="py-1.5 px-2 text-right tabular-nums">${formatMonto(total)}</td>
              <td className="hidden sm:table-cell" />
            </tr>
          </tfoot>
        </table>
      </div>
    )}
    </>
  );
}
